import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Action, ModuleId, Subject } from '@prody/shared';
import { EmptyState } from '~/design-system';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';

/* ------------------------------------------------------------------ *
 * Two different "no" answers, and they must not be confused.
 *
 *   module off for this tenant  → the feature does not exist here
 *   permission missing          → it exists, you cannot see it
 *
 * Collapsing both into a generic 404 is what makes people file
 * tickets. The second case names the fix: ask an owner to change
 * your role.
 *
 * This is a rendering guard, not a security boundary. The API
 * enforces the same rules; this exists so the app does not render a
 * half-broken screen and fire fetches that will 403.
 * ------------------------------------------------------------------ */

export function ModuleGuard({
  moduleId,
  requires,
  children,
}: {
  moduleId: ModuleId;
  requires: { subject: Subject; action: Action };
  children: ReactNode;
}) {
  const { t } = useTranslation('common');
  const { hasModule, can } = useBusiness();

  if (!hasModule(moduleId)) {
    return (
      <div className="pd-guard">
        <EmptyState title={t('errors.notFound')} body={t('errors.notFoundBody')} />
      </div>
    );
  }

  if (!can(requires.subject, requires.action)) {
    return (
      <div className="pd-guard">
        <EmptyState title={t('errors.forbidden')} body={t('errors.forbiddenBody')} />
      </div>
    );
  }

  return <>{children}</>;
}
